import React, { useEffect } from "react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { useParams } from "react-router-dom";
import { fetchBuyerRequestsDetailsAsync } from "../../../redux/features/requestManagementSlice";

const cardStyle =
  "border rounded-xl p-6 shadow-md bg-white mb-6 flex flex-col gap-4";

const labelStyle = "font-medium text-sm text-gray-500";
const valueStyle = "text-base font-medium text-gray-800";

const StarRating = ({ rating }: { rating: number }) => {
  const stars = Array.from({ length: 5 }, (_, i) => (
    <span key={i} className={i < rating ? "text-orange-500" : "text-gray-300"}>
      ★
    </span>
  ));
  return <div>{stars}</div>;
};

const StatusTag = ({ status }: { status: string }) => {
  const statusColors: Record<string, string> = {
    PENDING: "#EAB308",
    COMPLETED: "#22C55E",
    CANCELLED: "#EF4444",
    EXPIRED: "#6B7280",
    INPROGRESS: "#3B82F6",
    ACCEPTED: "#10B981",
    REJECTED: "#EF4444",
  };

  const bg = `${statusColors[status] || "#E5E7EB"}20`;
  const color = statusColors[status] || "#374151";
  const border = statusColors[status] || "#D1D5DB";

  return (
    <span
      style={{
        backgroundColor: bg,
        color,
        border: `1px solid ${border}`,
        borderRadius: "9999px",
        padding: "4px 12px",
        fontSize: "12px",
        fontWeight: 600,
        display: "inline-block",
        width: "fit-content",
        textTransform: "capitalize",
      }}
    >
      {status?.toLowerCase()}
    </span>
  );
};

const Detail = ({ label, value }: { label: string; value: any }) => (
  <div>
    <span className={labelStyle}>{label}:</span>
    <p className={valueStyle}>{value ?? "—"}</p>
  </div>
);

const RequestDetails = () => {
  const { buyerRequestDetails } = useAppSelector((state) => state.requests);
  const dispatch = useAppDispatch();
  const { id: requestId } = useParams();

  useEffect(() => {
    dispatch(fetchBuyerRequestsDetailsAsync(requestId));
  }, [requestId, dispatch]);

  const vendorResponses =
    buyerRequestDetails?.vendorBatches?.flatMap(
      (batch: any) => batch?.vendorResponses || []
    ) || [];

  // Optional: get the first contract found in the responses
  const contract =
    vendorResponses.find((res: any) => res?.contract)?.contract || null;

  return (
    <div className="p-6 mx-auto px-4 py-10 bg-gray-50 min-h-screen">
      <button
        className="text-gray-500 hover:text-gray-800 flex items-center font-bold mb-4 bg-gray-50 p-2 rounded"
        onClick={() => window.history.back()}
      >
        &larr; Back
      </button>

      {/* Request Summary */}
      <div className={cardStyle}>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Request Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Detail
            label="Product Description"
            value={buyerRequestDetails?.productDescription}
          />
          <Detail label="Quantity" value={buyerRequestDetails?.quantity} />
          <Detail label="Category" value={buyerRequestDetails?.categories} />
          <div>
            <span className={labelStyle}>Status:</span>
            <div className="mt-1">
              <StatusTag status={buyerRequestDetails?.status} />
            </div>
          </div>
          <Detail
            label="Created At"
            value={moment(buyerRequestDetails?.createdAt).format(
              "Do MMM YYYY, h:mm A"
            )}
          />
          <Detail
            label="Expires At"
            value={moment(buyerRequestDetails?.expiresAt).format(
              "Do MMM YYYY, h:mm A"
            )}
          />
        </div>
      </div>

      {/* Buyer Info */}
      <div className={cardStyle}>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Buyer Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Detail
            label="Full Name"
            value={buyerRequestDetails?.buyer?.fullName}
          />
          <Detail label="Email" value={buyerRequestDetails?.buyer?.email} />
          <Detail
            label="Phone Number"
            value={buyerRequestDetails?.buyer?.phoneNumber}
          />
        </div>
      </div>

      {/* Vendor Responses */}
      {vendorResponses.length > 0 && (
        <div className={cardStyle}>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Vendor Responses
          </h2>
          {vendorResponses.map((response: any) => (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t-2 py-4"
              key={response.id}
            >
              {/* Business Details */}
              <Detail
                label="Business Name"
                value={response?.vendor?.businessName}
              />
              <Detail
                label="Business Email"
                value={response?.vendor?.businessEmail}
              />
              <Detail
                label="Business Phone Number"
                value={response?.vendor?.businessPhoneNumber}
              />
              <Detail
                label="Business City"
                value={response?.vendor?.businessCity}
              />
              <Detail label="Vendor Name" value={response?.vendor?.fullName} />
              <Detail label="Vendor Email" value={response?.vendor?.email} />
              <Detail
                label="Vendor Phone Number"
                value={response?.vendor?.phoneNumber}
              />
              {/* Star rating */}
              <div>
                <span className={labelStyle}>Average Rating:</span>
                <div className="mt-1">
                  <StarRating
                    rating={Math.round(response?.vendor?.averageRating || 0)}
                  />
                </div>
              </div>

              <div>
                <span className={labelStyle}>Response Status:</span>
                <div className="mt-1">
                  <StatusTag status={response.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contract Details */}
      {contract && (
        <div className={cardStyle}>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contract Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Detail label="Title" value={contract?.title} />
            <Detail label="Code" value={contract?.contractCode} />
            <Detail label="Shipping State" value={contract?.shipoutState} />
            <Detail
              label="Shipping Date"
              value={moment(contract.shippingDate).format("Do MMM YYYY")}
            />
            <div>
              <span className={labelStyle}>Contract Status:</span>
              <div className="mt-1">
                <StatusTag status={contract?.status} />
              </div>
            </div>
            <div>
              <span className={labelStyle}>Buyer Approval:</span>
              <div className="mt-1">
                <StatusTag status={contract?.buyerApprovalStatus} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestDetails;
